import type { Bundle } from '@/ui/services/BundleService';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { MathHelper } from '@/utils/math-helper';

const SKUS_PARAM_SEPARATOR = ',';

export interface ShowcaseTaggedImage {
  showcase?: Array<{ productVariants?: Array<{ sku: string }> | null }> | null;
}

function getShowcaseSkus(image: ShowcaseTaggedImage): string[] {
  return (image.showcase ?? []).flatMap(
    (entry) => entry.productVariants?.map((variant) => variant.sku) ?? [],
  );
}

// Picks the bundle images that show the selected variants. An image tagged with
// product variants matches when all its tagged SKUs are selected; the most specific
// matches win. Untagged images always follow, and the result is never empty unless
// the input is.
export function selectBundleImages<T extends ShowcaseTaggedImage>(
  images: T[],
  selectedSkus: string[],
): T[] {
  const selected = new Set(selectedSkus);
  const untagged: T[] = [];
  const matches: Array<{ image: T; skuCount: number }> = [];

  images.forEach((image) => {
    const skus = new Set(getShowcaseSkus(image));
    if (skus.size === 0) {
      untagged.push(image);
      return;
    }

    if (Array.from(skus).every((sku) => selected.has(sku))) {
      matches.push({ image, skuCount: skus.size });
    }
  });

  const bestCount = Math.max(0, ...matches.map((match) => match.skuCount));
  const bestMatches = matches
    .filter((match) => match.skuCount === bestCount)
    .map((match) => match.image);
  const result = [...bestMatches, ...untagged];

  return result.length > 0 ? result : [...images];
}

export function parseBundleSkusParam(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }

  // Duplicates stay: a set may list the same product twice, and each copy names one slot.
  return value
    .split(SKUS_PARAM_SEPARATOR)
    .map((sku) => sku.trim())
    .filter((sku) => sku.length > 0);
}

export function formatBundleSkusParam(skus: string[]): string | undefined {
  return skus.length > 0 ? skus.join(SKUS_PARAM_SEPARATOR) : undefined;
}

export function pushBundleViewItem(bundle: Bundle): void {
  const currency = bundle.products[0]?.priceVariant?.currency;
  const roundedPrice = MathHelper.round(bundle.finalPrice);
  DataLayerHelper.pushViewItem(
    [
      {
        item_id: `bundle-${bundle.documentExtRef}`,
        item_name: bundle.name,
        item_brand: 'Nuki',
        price: roundedPrice,
        quantity: 1,
      },
    ],
    currency,
    roundedPrice,
  );
}
